import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const NPVCalculator = ({ apiBase }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [towers, setTowers] = useState([]);
  const [bhkConfigs, setBHKConfigs] = useState([]);
  const [clients, setClients] = useState([]);
  
  const [formData, setFormData] = useState({
    project_id: '',
    client_id: '',
    client_name: '',
    tower_id: '',
    tower_name: '',
    bhk_type: '',
    floor: 1,
    interest_rate: 8.0,
    consideration_value: 0,
    base_price: 0,
    super_area: 0,
    payment_scheme: 'standard',
    custom_payments: []
  });

  const [npvResults, setNpvResults] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  useEffect(() => {
    if (formData.project_id) {
      fetchProjectDetails(formData.project_id);
    }
  }, [formData.project_id]);

  useEffect(() => {
    if (formData.tower_id && formData.bhk_type) {
      computeConsideration();
    }
  }, [formData.tower_id, formData.bhk_type, formData.floor]);

  useEffect(() => {
    if (formData.consideration_value > 0 && formData.payment_scheme) {
      calculateNPV();
    }
  }, [formData.consideration_value, formData.payment_scheme, formData.interest_rate, formData.custom_payments]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${apiBase}/projects`);
      setProjects(response.data);
      if (response.data.length > 0 && !formData.project_id) {
        setFormData(prev => ({ ...prev, project_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${apiBase}/clients/list`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await axios.get(`${apiBase}/projects/${projectId}`);
      setSelectedProject(response.data);
      const twrs = response.data.towers || [];
      setTowers(twrs);
      
      const allBHKConfigs = [];
      twrs.forEach(tower => {
        (tower.bhk_configs || []).forEach(config => {
          allBHKConfigs.push({ ...config, tower_id: tower.id, tower_name: tower.tower_name });
        });
      });
      setBHKConfigs(allBHKConfigs);

      // Auto-select first tower and BHK
      if (twrs.length > 0) {
        const firstTower = twrs[0];
        const firstBHK = (firstTower.bhk_configs || [])[0];
        setFormData(prev => ({
          ...prev,
          tower_id: firstTower.id,
          tower_name: firstTower.tower_name,
          bhk_type: firstBHK ? firstBHK.bhk_type : ''
        }));
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const computeConsideration = () => {
    const config = bhkConfigs.find(c =>
      c.tower_id === parseInt(formData.tower_id) &&
      c.bhk_type === formData.bhk_type
    );
    if (!config) return;

    const floor = parseInt(formData.floor) || 1;
    const floorRisePerLevel = parseFloat(config.floor_rise_per_level) || selectedProject?.floor_rise_rate || 200;
    const carpetArea = config.carpet_area || 0;
    const pricePSF = config.price_per_sqft || selectedProject?.price_per_sqft || selectedProject?.base_rate_per_sqft || 27000;
    
    // Base price = carpet area × (PSF + floor rise)
    const effectivePSF = pricePSF + (floorRisePerLevel * (floor - 1));
    const basePrice = carpetArea * effectivePSF;
    const superArea = config.super_area || carpetArea;

    // Charges from master data
    const stampDuty = basePrice * ((selectedProject?.stamp_duty_rate || 6.0) / 100);
    const gst = basePrice * ((selectedProject?.gst_rate || 5.0) / 100);
    const regPct = basePrice * ((selectedProject?.registration_charge_rate || 1.0) / 100);
    const regFlat = selectedProject?.registration_flat_amount || 30000;
    const registration = Math.max(regPct, regFlat);
    const legalCharges = selectedProject?.legal_charges || 25000;
    const considerationValue = basePrice + stampDuty + gst + registration + legalCharges;

    setFormData(prev => ({
      ...prev,
      base_price: basePrice,
      super_area: superArea,
      consideration_value: Math.round(considerationValue)
    }));
  };

  const calculateNPV = () => {
    const scheme = formData.payment_scheme;
    const total = formData.consideration_value;
    const rate = formData.interest_rate / 100;
    const today = new Date();
    let payments = [];

    if (scheme === 'standard') {
      payments = [
        { date: new Date(today), amount: total * 0.10, type: 'Booking Amount' },
        { date: new Date(today.getTime() + 30*86400000), amount: total * 0.15, type: 'Within 30 Days' },
        { date: new Date(today.getTime() + 90*86400000), amount: total * 0.15, type: 'Within 90 Days' },
        { date: new Date(today.getTime() + 180*86400000), amount: total * 0.15, type: '6 Months' },
        { date: new Date(today.getTime() + 365*86400000), amount: total * 0.15, type: '12 Months' },
        { date: new Date(today.getTime() + 545*86400000), amount: total * 0.15, type: '18 Months' },
        { date: new Date(today.getTime() + 730*86400000), amount: total * 0.15, type: 'On Possession' },
      ];
    } else if (scheme === '20_80') {
      payments = [
        { date: new Date(today), amount: total * 0.20, type: 'Booking + Down Payment' },
        { date: new Date(today.getTime() + 730*86400000), amount: total * 0.80, type: 'On Possession' },
      ];
    } else if (scheme === 'full_advance') {
      payments = [
        { date: new Date(today), amount: total, type: 'Full Payment' },
      ];
    } else if (scheme === 'early') {
      payments = [
        { date: new Date(today), amount: total * 0.25, type: 'Booking' },
        { date: new Date(today.getTime() + 30*86400000), amount: total * 0.25, type: '30 Days' },
        { date: new Date(today.getTime() + 60*86400000), amount: total * 0.25, type: '60 Days' },
        { date: new Date(today.getTime() + 90*86400000), amount: total * 0.25, type: '90 Days' },
      ];
    } else if (scheme === 'bespoke' && formData.custom_payments.length > 0) {
      payments = formData.custom_payments.map(p => ({
        date: new Date(p.date),
        amount: p.amount || 0,
        type: p.label || 'Custom'
      }));
    }

    // Calculate NPV
    let npv = 0;
    let totalInterestPaid = 0;
    let totalInterestSaved = 0;

    const schedule = payments.map(p => {
      const daysFromNow = Math.round((p.date - today) / 86400000);
      const years = daysFromNow / 365;
      const pv = p.amount / Math.pow(1 + rate, years);
      const interest = p.amount - pv;

      if (daysFromNow > 0) {
        totalInterestPaid += Math.abs(interest);
      } else {
        totalInterestSaved += Math.abs(interest);
      }

      npv += pv;
      return { ...p, daysFromNow, presentValue: pv, interest };
    });

    setPaymentSchedule(schedule);
    setNpvResults({
      npv: Math.round(npv),
      consideration_value: total,
      total_interest_paid: Math.round(totalInterestPaid),
      total_interest_saved: Math.round(totalInterestSaved),
      net_interest: Math.round(totalInterestPaid - totalInterestSaved)
    });

    // Compare with standard scheme if not already standard
    if (scheme !== 'standard') {
      let stdNPV = 0;
      const stdPayments = [0.10, 0.15, 0.15, 0.15, 0.15, 0.15, 0.15];
      const stdDays = [0, 30, 90, 180, 365, 545, 730];
      stdDays.forEach((d, i) => {
        stdNPV += (total * stdPayments[i]) / Math.pow(1 + rate, d / 365);
      });
      setComparison({
        standard_npv: Math.round(stdNPV),
        current_npv: Math.round(npv),
        difference: Math.round(npv - stdNPV),
        savings: npv < stdNPV
      });
    } else {
      setComparison(null);
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val || 0);

  const uniqueBHKTypes = [...new Set(bhkConfigs.filter(c => c.tower_id === parseInt(formData.tower_id)).map(c => c.bhk_type))];

  return (
    <div>
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title">NPV Calculator</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
          Calculate Net Present Value for different payment schemes. All charges pulled from Master Data.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          {/* Project */}
          <div className="form-group">
            <label className="form-label">Project</label>
            <select className="form-input" value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}>
              <option value="">Select Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Tower */}
          <div className="form-group">
            <label className="form-label">Tower / Wing</label>
            <select className="form-input" value={formData.tower_id} onChange={(e) => {
              const t = towers.find(tw => tw.id === parseInt(e.target.value));
              setFormData({ ...formData, tower_id: e.target.value, tower_name: t ? t.tower_name : '', bhk_type: '' });
            }}>
              <option value="">Select Tower</option>
              {towers.map(t => <option key={t.id} value={t.id}>{t.tower_name}</option>)}
            </select>
          </div>

          {/* BHK */}
          <div className="form-group">
            <label className="form-label">Configuration</label>
            <select className="form-input" value={formData.bhk_type} onChange={(e) => setFormData({ ...formData, bhk_type: e.target.value })}>
              <option value="">Select BHK</option>
              {uniqueBHKTypes.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* Floor */}
          <div className="form-group">
            <label className="form-label">Floor</label>
            <input type="number" min="1" max="50" className="form-input" value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) || 1 })} />
          </div>

          {/* Client */}
          <div className="form-group">
            <label className="form-label">Client (Optional)</label>
            <select className="form-input" value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}>
              <option value="">Select Client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</option>)}
            </select>
          </div>

          {/* Interest Rate */}
          <div className="form-group">
            <label className="form-label">Interest Rate (%)</label>
            <input type="number" step="0.5" min="0" max="25" className="form-input" value={formData.interest_rate} onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) || 8.0 })} />
          </div>
        </div>

        {/* Computed Values Display */}
        {formData.base_price > 0 && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Base Price</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>₹{formatCurrency(formData.base_price)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Carpet Area</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>{formData.super_area} sq.ft</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Stamp Duty ({selectedProject?.stamp_duty_rate || 6}%)</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>₹{formatCurrency(formData.base_price * ((selectedProject?.stamp_duty_rate || 6) / 100))}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>GST ({selectedProject?.gst_rate || 5}%)</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>₹{formatCurrency(formData.base_price * ((selectedProject?.gst_rate || 5) / 100))}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Total Consideration</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#1e40af' }}>₹{formatCurrency(formData.consideration_value)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Scheme */}
        <div style={{ marginTop: '16px' }}>
          <label className="form-label">Payment Scheme</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'standard', label: 'Standard (10-15-15-15-15-15-15)' },
              { id: '20_80', label: '20:80 Scheme' },
              { id: 'full_advance', label: 'Full Advance' },
              { id: 'early', label: 'Early Payment (25×4)' },
              { id: 'bespoke', label: 'Custom / Bespoke' },
            ].map(s => (
              <button key={s.id} type="button" onClick={() => setFormData({ ...formData, payment_scheme: s.id })}
                className={`btn ${formData.payment_scheme === s.id ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '12px', padding: '6px 12px' }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bespoke Payments */}
        {formData.payment_scheme === 'bespoke' && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0 }}>Custom Payments</h4>
              <button className="btn btn-secondary" style={{ fontSize: '12px' }} onClick={() =>
                setFormData({ ...formData, custom_payments: [...formData.custom_payments, { date: new Date().toISOString().split('T')[0], amount: 0, label: '' }] })}>
                + Add Payment
              </button>
            </div>
            {formData.custom_payments.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                <input type="date" className="form-input" value={p.date} onChange={(e) => {
                  const cp = [...formData.custom_payments]; cp[i].date = e.target.value; setFormData({ ...formData, custom_payments: cp });
                }} />
                <input type="number" className="form-input" placeholder="Amount" value={p.amount} onChange={(e) => {
                  const cp = [...formData.custom_payments]; cp[i].amount = parseFloat(e.target.value) || 0; setFormData({ ...formData, custom_payments: cp });
                }} />
                <input type="text" className="form-input" placeholder="Label" value={p.label} onChange={(e) => {
                  const cp = [...formData.custom_payments]; cp[i].label = e.target.value; setFormData({ ...formData, custom_payments: cp });
                }} />
                <button className="btn btn-secondary" style={{ color: '#dc2626', padding: '4px 8px' }} onClick={() => {
                  const cp = formData.custom_payments.filter((_, idx) => idx !== i); setFormData({ ...formData, custom_payments: cp });
                }}>✕</button>
              </div>
            ))}
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              Total: ₹{formatCurrency(formData.custom_payments.reduce((sum, p) => sum + (p.amount || 0), 0))} |
              Required: ₹{formatCurrency(formData.consideration_value)}
            </div>
          </div>
        )}
      </div>

      {/* NPV Results */}
      {npvResults && (
        <>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title">NPV Calculation Results</h2>
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <div className="stat-label" style={{ color: 'rgba(255,255,255,0.9)' }}>Net Present Value</div>
                <div className="stat-value" style={{ color: 'white', fontSize: '1.8rem' }}>₹{formatCurrency(npvResults.npv)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Consideration Value</div>
                <div className="stat-value">₹{formatCurrency(npvResults.consideration_value)}</div>
              </div>
              <div className="stat-card" style={{ background: npvResults.total_interest_paid > npvResults.total_interest_saved ? '#fee2e2' : '#d1fae5' }}>
                <div className="stat-label">Total Interest Paid</div>
                <div className="stat-value" style={{ color: '#dc2626' }}>₹{formatCurrency(npvResults.total_interest_paid)}</div>
              </div>
              <div className="stat-card" style={{ background: '#d1fae5' }}>
                <div className="stat-label">Interest Saved</div>
                <div className="stat-value" style={{ color: '#059669' }}>₹{formatCurrency(npvResults.total_interest_saved)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Net Interest</div>
                <div className="stat-value" style={{ color: npvResults.net_interest > 0 ? '#dc2626' : '#059669' }}>₹{formatCurrency(npvResults.net_interest)}</div>
              </div>
              {comparison && (
                <div className="stat-card" style={{ background: comparison.savings ? '#d1fae5' : '#fee2e2' }}>
                  <div className="stat-label">vs Standard</div>
                  <div className="stat-value" style={{ color: comparison.savings ? '#059669' : '#dc2626' }}>
                    {comparison.savings ? 'Save' : 'Pay'} ₹{formatCurrency(Math.abs(comparison.difference))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Schedule */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title">Payment Schedule</h2>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Days</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Present Value</th>
                    <th>Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSchedule.map((p, i) => (
                    <tr key={i}>
                      <td>{new Date(p.date).toLocaleDateString('en-IN')}</td>
                      <td>{p.daysFromNow} days</td>
                      <td><strong>₹{formatCurrency(p.amount)}</strong></td>
                      <td>{p.type}</td>
                      <td>₹{formatCurrency(p.presentValue)}</td>
                      <td style={{ color: p.daysFromNow > 0 ? '#dc2626' : '#059669' }}>
                        {p.daysFromNow > 0 ? '+' : '-'}₹{formatCurrency(Math.abs(p.interest))}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f9fafb', fontWeight: 600 }}>
                    <td colSpan="2"><strong>Total</strong></td>
                    <td><strong>₹{formatCurrency(paymentSchedule.reduce((s, p) => s + p.amount, 0))}</strong></td>
                    <td></td>
                    <td><strong>₹{formatCurrency(npvResults.npv)}</strong></td>
                    <td><strong>₹{formatCurrency(npvResults.net_interest)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div className="card">
              <h3 className="card-title">Payment vs Present Value</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paymentSchedule.map((p, i) => ({ name: `#${i + 1}`, amount: p.amount, pv: p.presentValue }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
                  <Legend />
                  <Bar dataKey="amount" fill="#2563eb" name="Payment Amount" />
                  <Bar dataKey="pv" fill="#10b981" name="Present Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="card-title">Cumulative Payments</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={paymentSchedule.map((p, i) => ({
                  name: `#${i + 1}`,
                  cumulative: paymentSchedule.slice(0, i + 1).reduce((s, x) => s + x.amount, 0),
                  cumulativePV: paymentSchedule.slice(0, i + 1).reduce((s, x) => s + x.presentValue, 0)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
                  <Legend />
                  <Line type="monotone" dataKey="cumulative" stroke="#2563eb" name="Cumulative Amount" />
                  <Line type="monotone" dataKey="cumulativePV" stroke="#10b981" name="Cumulative NPV" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NPVCalculator;
